import {
  Alert,
  Box,
  Button,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import { useFormat, type Formatters } from "../../i18n/format";
import {
  statementToCsv,
  type ContentStatement,
  type StatementLineKind,
} from "@shared/content-statement";

/**
 * One rev-share statement (`WL-6`, and since consumer TVOD slice F its
 * purchase-portal lines) - drawn the same way for the provider on its own
 * Revenue page and for Nenda staff on the provider's portal dialog, so the
 * two can never disagree about what a number means.
 *
 * The rules it draws by, all from `@shared/content-statement`:
 *
 * - the period is ON the page, in words (with no dates the server answers
 *   for the last full calendar month - a figure that reads like a lifetime
 *   total if nobody says otherwise);
 * - one row of figures PER CURRENCY, never a sum across them;
 * - an unagreed share says so, never "0";
 * - a refund is its own negative line, in the month it happened;
 * - invoiced money (organisation plans, billed through Upsales) is shown
 *   beside the totals as "Invoiced, awaiting payment" and never inside gross,
 *   net or the shares - nothing in Foyer can confirm it was paid.
 */

export type StatementRange = { from: string; to: string };

/** Money as a partner reads it, not as it is stored. */
export function money(
  fmt: Formatters,
  amount: number,
  currency: string
): string {
  return `${fmt.number(amount, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} ${currency}`;
}

/** An ISO instant as a date input wants it. */
export function dayOf(iso: string): string {
  return iso.slice(0, 10);
}

export function StatementView({
  statement,
  range,
  onRangeChange,
  onApply,
  shareLabel,
  csvName,
}: {
  statement: ContentStatement;
  range: StatementRange | null;
  onRangeChange: (range: StatementRange) => void;
  onApply: (range: StatementRange) => void;
  /** Whose share the owner half is, in the reader's words. */
  shareLabel?: string;
  /** File name for the CSV download (no extension). */
  csvName: string;
}) {
  const { t } = useTranslation(["offers", "common"]);
  const fmt = useFormat();
  const ownerShareLabel = shareLabel ?? t("statementView.yourShare");
  const kindLabel = (kind: StatementLineKind) =>
    t(`statementView.kind.${kind}`);
  const panel = {
    background: tokens.surface,
    border: `1px solid ${tokens.divider}`,
    borderRadius: 2,
    p: 2.5,
  };

  const download = () => {
    const blob = new Blob([statementToCsv(statement)], {
      type: "text/csv;charset=utf-8",
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${csvName}-${dayOf(statement.from)}-${dayOf(statement.to)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={panel}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "flex-end" }}
        sx={{ mb: 2 }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontWeight: "fontWeightSemibold" }}>
            {t("statementView.title")}
          </Typography>
          {/* 🚨 The period, in words. With no dates the server answers for
              the last full calendar month — a figure that reads exactly like
              a lifetime total if nobody says otherwise. */}
          <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
            {t("statementView.period", {
              from: fmt.date(statement.from),
              to: fmt.date(statement.to),
            })}
          </Typography>
        </Box>
        <TextField
          size="small"
          type="date"
          label={t("statementView.from")}
          value={range?.from ?? ""}
          onChange={(e) =>
            onRangeChange({ from: e.target.value, to: range?.to ?? "" })
          }
          InputLabelProps={{ shrink: true }}
          inputProps={{ "data-testid": "statement-from" }}
        />
        <TextField
          size="small"
          type="date"
          label={t("statementView.to")}
          value={range?.to ?? ""}
          onChange={(e) =>
            onRangeChange({ from: range?.from ?? "", to: e.target.value })
          }
          onBlur={() => range && onApply(range)}
          InputLabelProps={{ shrink: true }}
          inputProps={{ "data-testid": "statement-to" }}
        />
      </Stack>

      {statement.totals.length === 0 ? (
        <Typography sx={{ color: tokens.inkMuted }}>
          {t("statementView.nothingEarned")}
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {statement.totals.map((total) => (
            <Stack
              key={total.currency}
              direction="row"
              flexWrap="wrap"
              spacing={3}
              data-testid={`total-${total.currency}`}
            >
              <Figure
                label={t("statementView.gross")}
                value={money(fmt, total.gross, total.currency)}
              />
              {total.reversed > 0 ? (
                <Figure
                  label={t("statementView.refunded")}
                  value={money(fmt, -total.reversed, total.currency)}
                />
              ) : null}
              <Figure
                label={t("statementView.net")}
                value={money(fmt, total.net, total.currency)}
                strong
              />
              {/* Unagreed is not zero. A split neither side has agreed is
                  not a number this page may invent. */}
              {total.ownerShare === null ? (
                <Figure
                  label={ownerShareLabel}
                  value={t("statementView.notAgreed")}
                />
              ) : (
                <>
                  <Figure
                    label={ownerShareLabel}
                    value={money(fmt, total.ownerShare, total.currency)}
                    strong
                  />
                  <Figure
                    label={t("statementView.nendasShare")}
                    value={money(fmt, total.nendaShare ?? 0, total.currency)}
                  />
                </>
              )}
              {/* Billed but not confirmed paid: beside the totals, never in
                  them. A share of money that may never arrive is a debt.
                  Truthy, not `!== 0`: an API from before this field sends
                  none, and a CMS deployed first must not crash on it. */}
              {total.invoiced ? (
                <Figure
                  label={t("statementView.invoicedAwaiting")}
                  value={money(fmt, total.invoiced, total.currency)}
                  testId={`invoiced-${total.currency}`}
                />
              ) : null}
            </Stack>
          ))}
        </Stack>
      )}

      {statement.unreconciled > 0 ? (
        <Alert severity="info" sx={{ mt: 2 }}>
          {t("statementView.unreconciled", { count: statement.unreconciled })}
        </Alert>
      ) : null}

      {statement.lines.length > 0 ? (
        <Box sx={{ mt: 2.5 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1 }}
          >
            <Typography
              variant="micro"
              sx={{ typography: "eyebrow", color: tokens.inkFaint }}
            >
              {t("statementView.lines", { count: statement.lines.length })}
            </Typography>
            <Button
              size="small"
              startIcon={<DownloadIcon fontSize="small" />}
              onClick={download}
              data-testid="statement-csv"
            >
              {t("statementView.exportCsv")}
            </Button>
          </Stack>
          <TableContainer sx={{ maxHeight: 360 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    {t("statementView.columns.date")}
                  </TableCell>
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    {t("statementView.columns.source")}
                  </TableCell>
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    {t("statementView.columns.reference")}
                  </TableCell>
                  <TableCell sx={{ color: tokens.inkMuted }}>
                    {t("statementView.columns.title")}
                  </TableCell>
                  <TableCell align="right" sx={{ color: tokens.inkMuted }}>
                    {t("statementView.columns.amount")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statement.lines.map((line, i) => (
                  <TableRow
                    key={`${line.ref}-${line.assetIds.join(",")}-${i}`}
                    data-testid={`statement-line-${i}`}
                  >
                    <TableCell>{fmt.date(line.at)}</TableCell>
                    <TableCell>
                      {kindLabel(line.kind)}
                      {line.reversal ? t("statementView.refundSuffix") : ""}
                      {line.status === "invoiced" ? (
                        <Typography
                          variant="caption"
                          sx={{ color: tokens.inkFaint, display: "block" }}
                        >
                          {t("statementView.invoicedLine")}
                        </Typography>
                      ) : null}
                    </TableCell>
                    {line.description ? (
                      <TableCell>{line.description}</TableCell>
                    ) : (
                      <TableCell
                        sx={{
                          fontFamily: "fontFamilyMono",
                          typography: "meta",
                        }}
                      >
                        {line.ref}
                      </TableCell>
                    )}
                    <TableCell
                      sx={{ fontFamily: "fontFamilyMono", typography: "meta" }}
                    >
                      {line.assetIds.length === 0
                        ? t("common:labels.emptyValue")
                        : line.assetIds.length === 1
                          ? line.assetIds[0]
                          : t("statementView.titleCount", {
                              count: line.assetIds.length,
                            })}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{
                        color: line.amount < 0 ? tokens.error : undefined,
                        // Not money that exists yet: drawn quieter than a paid line.
                        fontStyle:
                          line.status === "invoiced" ? "italic" : undefined,
                      }}
                    >
                      {money(fmt, line.amount, line.currency)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ) : null}
    </Box>
  );
}

function Figure({
  label,
  value,
  strong,
  testId,
}: {
  label: string;
  value: string;
  strong?: boolean;
  testId?: string;
}) {
  return (
    <Box data-testid={testId}>
      <Typography variant="caption" sx={{ color: tokens.inkFaint }}>
        {label}
      </Typography>
      <Typography
        sx={{
          typography: strong ? "headline" : "title",
          color: tokens.ink,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}
